import { AnimatePresence, motion } from 'framer-motion';
import { Check, ListTodo, Plus, ShoppingCart, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { SHOPPING_CATEGORIES, TODO_CATEGORIES, useLists, type TaskList } from '../../store/lists';

export function Lists({ interactive = true }: { interactive?: boolean }) {
  const lists = useLists((s) => s.lists);
  const [activeId, setActiveId] = useState(lists[0]?.id ?? 'todo');
  const list = lists.find((l) => l.id === activeId) ?? lists[0];
  if (!list) return null;

  return (
    <div className="glass p-4">
      <div className="mb-3 flex gap-1" role="tablist">
        {lists.map((l) => {
          const Icon = l.kind === 'shopping' ? ShoppingCart : ListTodo;
          const open = l.items.filter((i) => !i.done).length;
          return (
            <button
              key={l.id}
              role="tab"
              aria-selected={l.id === list.id}
              onClick={() => setActiveId(l.id)}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-sm transition ${
                l.id === list.id ? 'bg-[var(--accent)] text-black' : 'opacity-70 hover:opacity-100'
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden /> {l.name}
              {open > 0 && <span className="tabular-nums opacity-80">· {open}</span>}
            </button>
          );
        })}
      </div>
      <ListBody list={list} interactive={interactive} />
    </div>
  );
}

function ListBody({ list, interactive }: { list: TaskList; interactive: boolean }) {
  const { addItem, toggleItem, removeItem, clearDone } = useLists();
  const categories = list.kind === 'shopping' ? SHOPPING_CATEGORIES : TODO_CATEGORIES;
  const [text, setText] = useState('');
  const [category, setCategory] = useState(categories[0] ?? 'Other');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    addItem(list.id, text, category);
    setText('');
  };

  // Open items first, grouped by category; done items sink to the bottom.
  const sorted = [...list.items].sort(
    (a, b) => Number(a.done) - Number(b.done) || a.category.localeCompare(b.category) || a.createdAt - b.createdAt,
  );

  return (
    <div>
      {sorted.length === 0 && <div className="muted mb-2 text-sm">Nothing here yet.</div>}
      <ul className="max-h-72 space-y-1 overflow-y-auto pr-1">
        <AnimatePresence initial={false}>
          {sorted.map((item) => (
            <motion.li
              key={item.id}
              layout
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: 40 }}
              transition={{ duration: 0.25 }}
              className="group flex items-center gap-2"
            >
              <button
                onClick={() => toggleItem(list.id, item.id)}
                aria-label={item.done ? `Mark ${item.text} as not done` : `Mark ${item.text} as done`}
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                  item.done ? 'border-[var(--accent)] bg-[var(--accent)] text-black' : 'border-[var(--border)]'
                }`}
              >
                <AnimatePresence>
                  {item.done && (
                    <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
                      <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>
              <span className={`min-w-0 flex-1 truncate transition ${item.done ? 'muted line-through' : ''}`}>
                {item.text}
              </span>
              <span className="muted hidden text-xs sm:inline">{item.category}</span>
              {interactive && (
                <button
                  onClick={() => removeItem(list.id, item.id)}
                  aria-label={`Remove ${item.text}`}
                  className="opacity-0 transition group-hover:opacity-60 hover:!opacity-100 focus:opacity-100"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              )}
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
      {interactive && (
        <form onSubmit={submit} className="mt-3 flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={list.kind === 'shopping' ? 'Add to shopping…' : 'Add a task…'}
            maxLength={200}
            className="min-w-0 flex-1 rounded-lg border border-[var(--border)] bg-transparent px-3 py-1.5 text-sm outline-none"
            aria-label="New item"
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded-lg border border-[var(--border)] bg-[var(--surface-strong)] px-2 text-sm"
            aria-label="Category"
          >
            {categories.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <button type="submit" className="rounded-lg bg-[var(--accent)] px-2 text-black" aria-label="Add item">
            <Plus className="h-4 w-4" aria-hidden />
          </button>
        </form>
      )}
      {list.items.some((i) => i.done) && interactive && (
        <button onClick={() => clearDone(list.id)} className="muted mt-2 text-xs underline-offset-2 hover:underline">
          Clear completed
        </button>
      )}
    </div>
  );
}
